import React from "react";
import classes from "../RollStandDeviceView.module.scss";
import rollStandImage from "../../../assets/img/roll-stand.svg";
import {resolveSensorColor} from "./HeatMap";

// ---- Dot positions --------------------------------------------------
// Every dot position is a {left, top} PERCENTAGE of the image's own
// width/height, so dots stay correctly placed no matter how large the
// image renders.
//
// roll-stand.svg's viewBox is now the cropped "26 46 496 152" (was
// "0 0 605 250"). The <g id="rs-roller"> definition itself didn't change —
// still translated to (96,90) for the upper roller and (96,154) for the
// lower one, barrel spanning local x 0–345 — so against the new viewBox
// that's roughly left 14%/right 84% of the image width, and the lower
// roller's centre is at (154-46)/152 ≈ 71% of the image height. Dots sit
// on the lower roller, inset a bit from those raw edges (BARREL_LEFT/
// RIGHT_PERCENT below) to stay clear of the end-cap/coupling details —
// tuned in-browser, not pure geometry, so nudge those two constants
// (and ROLLER_TOP_PERCENT) directly if it still looks off.
//
// Dots are spaced with the two endpoints (BARREL_LEFT/RIGHT_PERCENT)
// always occupied by the first/last sensor — e.g. 3 sensors land at
// left/middle/right exactly, not inset further inward.
//
// To move every dot for a given count at once, nudge BARREL_LEFT/RIGHT/
// ROLLER_TOP_PERCENT below. To move ONE dot on its own (e.g. a channel
// whose sensors genuinely aren't evenly spaced), add an override entry
// to DOT_POSITIONS for that specific count — anything not listed there
// falls back to even spacing automatically.
const BARREL_LEFT_PERCENT = 21;
const BARREL_RIGHT_PERCENT = 75;
const ROLLER_TOP_PERCENT = 70;

// Evenly spaced within [BARREL_LEFT_PERCENT, BARREL_RIGHT_PERCENT], with
// the first/last sensor always landing exactly on those two edges (e.g.
// 3 sensors → left/middle/right, not inset further inward).
const evenlySpacedPositions = (count: number) => {
    if (count <= 1) {
        return [{left: `${(BARREL_LEFT_PERCENT + BARREL_RIGHT_PERCENT) / 2}%`, top: `${ROLLER_TOP_PERCENT}%`}];
    }
    const span = BARREL_RIGHT_PERCENT - BARREL_LEFT_PERCENT;
    return Array.from({length: count}, (_, i) => ({
        left: `${BARREL_LEFT_PERCENT + (i / (count - 1)) * span}%`,
        top: `${ROLLER_TOP_PERCENT}%`,
    }));
};

// Hand-tuned overrides, keyed by sensor count — empty for now, since even
// spacing (above) already matches what's been checked in-browser.
const DOT_POSITIONS: Record<number, Array<{ left: string; top: string }>> = {};

const getDotPositions = (count: number) => DOT_POSITIONS[count] ?? evenlySpacedPositions(count);

interface RollerDiagramProps {
    sensors: any[];
    onSensorClick: (sensorId: any) => void;
    selectedSensors: any[];
}

const RollerDiagram: React.FC<RollerDiagramProps> = ({sensors, onSensorClick, selectedSensors}) => {
    if (!sensors || sensors.length === 0) {
        return null;
    }

    const positions = getDotPositions(sensors.length);
    const safeSelectedSensors = Array.isArray(selectedSensors) ? selectedSensors : [];

    return (
        <div className={classes.rollerWrap}>
            <img src={rollStandImage} alt="Roll stand" className={classes.rollerImage}/>
            {sensors.map((sensor, index) => {
                const position = positions[index] ?? positions[positions.length - 1];
                const isSelected = safeSelectedSensors.some((s) => s.Id === sensor.Id);
                const color = resolveSensorColor(sensor.Value, sensor.Status, sensor.Meta?.Units);
                return (
                    <div
                        key={sensor.Id}
                        className={classes.rollerDot}
                        style={{
                            left: position.left,
                            top: position.top,
                            background: color,
                            ...(isSelected ? {boxShadow: "0 0 0 2px #000"} : {}),
                        }}
                        title={sensor.Name}
                        onClick={() => onSensorClick(sensor.Id)}
                    />
                );
            })}
        </div>
    );
};

export default RollerDiagram;
